/**
 * Frontend contract types — mirror the API envelope exactly.
 * Backend is the source of truth; keep these in lockstep with src/api.
 */

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: string };

export type Area = 'st-pete' | 'tampa' | 'clearwater' | 'brandon' | 'sarasota' | 'online' | 'other';

export type Category = 'chamber' | 'bni' | 'meetup' | 'nonprofit' | 'mixer' | 'workshop' | 'other';

export interface EventDto {
  id: string;
  slug: string;
  title: string;
  description: string;
  startsAt: number; // unix seconds
  endsAt: number | null;
  locationName: string;
  address: string;
  area: Area;
  cost: string;
  isFree: boolean;
  category: Category;
  organizerName: string;
  organizerWebsite: string;
  registerUrl: string;
  imageUrl: string;
  status: string;
  spotlight: boolean;
  spotlightEnds: number | null;
  seriesId: string | null;
  dateLabel: string; // 'Wed, Jul 8'
  timeLabel: string; // '9:00 AM'
  endTimeLabel: string | null;
  dayKey: string; // '2026-07-08'
  iso: string; // '2026-07-08T09:00:00-04:00'
}

export interface Recurrence {
  pattern: 'weekly' | 'biweekly' | 'monthly-nth-weekday';
  dayOfWeek: number; // 0–6, Sunday = 0
  nth?: number; // 1–5 (5 = last), monthly only
  until?: string; // 'YYYY-MM-DD'
}

export interface SubmissionInput {
  title: string;
  date: string; // 'YYYY-MM-DD'
  startTime: string; // 'HH:MM' 24h
  endTime?: string;
  locationName: string;
  address: string;
  area: Area;
  cost: string;
  isFree: boolean;
  category: Category;
  organizerName: string;
  organizerWebsite: string;
  registerUrl: string;
  description: string; // ≤75 words
  recurrence?: Recurrence;
}

export interface SubmitResult {
  submitted: number;
  results: { title: string; editUrl: string }[];
}

export interface SiteConfig {
  turnstileSiteKey: string;
  siteName: string;
}

export interface OrganizationDto {
  id: string;
  name: string;
  website: string;
  contactName: string;
  contactEmail: string;
  phone: string;
  notes: string;
  status: string;
  createdAt: number;
}

export type SpotlightRate = '25wk' | '60mo' | 'comp';
export type PaymentMethod = 'check' | 'paypal' | 'comp';

export interface SpotlightPurchaseDto {
  id: string;
  organizationId: string;
  organizationName: string;
  eventId: string;
  eventTitle: string;
  rate: SpotlightRate;
  startsAt: number;
  endsAt: number;
  paymentMethod: PaymentMethod;
  paymentReceived: boolean;
  notes: string;
}

export interface AdminOverview {
  pendingCount: number;
  upcomingCount: number;
  subscriberCount: number;
  activeSpotlights: EventDto[];
  pending: EventDto[];
}

export interface SubscriberDto {
  email: string;
  createdAt: number;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export function emptySubmission(): SubmissionInput {
  return {
    title: '',
    date: '',
    startTime: '',
    endTime: '',
    locationName: '',
    address: '',
    area: 'tampa',
    cost: '',
    isFree: true,
    category: 'mixer',
    organizerName: '',
    organizerWebsite: '',
    registerUrl: '',
    description: '',
  };
}
